/**
 * Cenários pessimista, provável e otimista (seção 10.11 do planejamento).
 *
 * No cenário pessimista o estoque não vendido continua consumindo capital: o
 * denominador do ROI permanece o capital necessário integral, enquanto o
 * numerador cai. Por isso o ROI despenca mais que proporcionalmente ao lucro.
 */

import { BPS, aplicarBps } from './inteiros.js';
import { analisar } from './viabilidade.js';
import type { EntradaAnalise, ResultadoAnalise, TipoCenario } from './tipos.js';

export interface ParametrosCenarios {
  /** Queda aplicada ao preço no cenário pessimista. 1000 = 10%. */
  deltaPessimistaBps: number;
  /** Alta aplicada ao preço no cenário otimista. 1000 = 10%. */
  deltaOtimistaBps: number;
  /** Percentual da quantidade vendável que se espera vender no pessimista. */
  pctVendidoPessimistaBps: number;
}

export const CENARIOS_PADRAO: ParametrosCenarios = {
  deltaPessimistaBps: 1_000,
  deltaOtimistaBps: 1_000,
  pctVendidoPessimistaBps: 7_000,
};

export interface Cenario {
  tipo: TipoCenario;
  precoVendaCents: number;
  pctVendidoBps: number;
  resultado: ResultadoAnalise;
}

export function calcularCenarios(base: EntradaAnalise, p: ParametrosCenarios = CENARIOS_PADRAO): Cenario[] {
  const variacoes: ReadonlyArray<readonly [TipoCenario, number, number]> = [
    ['PESSIMISTA', aplicarBps(base.precoVendaCents, BPS - p.deltaPessimistaBps), p.pctVendidoPessimistaBps],
    ['PROVAVEL', base.precoVendaCents, BPS],
    ['OTIMISTA', aplicarBps(base.precoVendaCents, BPS + p.deltaOtimistaBps), BPS],
  ];

  return variacoes.map(([tipo, precoVendaCents, pctVendidoBps]) => ({
    tipo,
    precoVendaCents,
    pctVendidoBps,
    resultado: analisar({ ...base, precoVendaCents, pctVendidoBps }),
  }));
}

/** ROI do cenário pessimista, para alimentar a RN-088. Null quando faltam parâmetros. */
export function roiPessimista(cenarios: readonly Cenario[]): number | null {
  const c = cenarios.find((x) => x.tipo === 'PESSIMISTA');
  return c && c.resultado.completo ? c.resultado.roiBps : null;
}

/**
 * Velocidade de venda e giro de estoque (seção 10.9).
 *
 * Exige DUAS coletas do mesmo anúncio em datas diferentes: a quantidade vendida
 * informada pelo marketplace é acumulada desde a criação do anúncio, não um
 * valor por período. Com uma leitura só, a velocidade é indeterminada — e o
 * sistema devolve null, nunca zero (RN-086).
 */
export function giroEstoqueDias(
  vendidosColeta1: number,
  vendidosColeta2: number,
  diasEntreColetas: number,
  quantidadeMilli: number,
): number | null {
  if (diasEntreColetas <= 0) return null;
  const vendidosNoPeriodo = vendidosColeta2 - vendidosColeta1;
  if (vendidosNoPeriodo <= 0) return null;
  const velocidadeMilliPorDia = Math.round((vendidosNoPeriodo * 1000) / diasEntreColetas);
  if (velocidadeMilliPorDia <= 0) return null;
  return Math.ceil(quantidadeMilli / velocidadeMilliPorDia);
}
